import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Container, Header, Segment, Step, Icon, Message, Loader, Divider, Grid, Label, Table } from 'semantic-ui-react'
import api from '../api'
import SectionHeader from './section_header'

const TrackOrderPage = () => {
  const { orderId } = useParams()
  const [loading, setLoading] = useState(true)
  const [trackingData, setTrackingData] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Track page view with Faro
    if (window.faroTracker) {
      window.faroTracker.trackPageView(`/track/${orderId}`)
      window.faroTracker.trackEvent('order_tracking_viewed', {
        order_id: orderId,
        timestamp: new Date().toISOString()
      })
      
      // Start user action for order tracking
      if (typeof window.faroTracker.startUserAction === 'function') {
        window.faroTracker.startUserAction('order-tracking-view', {
          order_id: orderId
        }, {
          triggerName: 'page_load',
          importance: 'normal'
        });
      }
    }

    // Fetch tracking data
    const fetchTrackingData = async () => {
      try {
        setLoading(true)
        const startTime = Date.now()
        
        // Call tracking API
        const response = await api.trackOrder(orderId)
        
        const duration = Date.now() - startTime
        
        if (response.status === 200) {
          setTrackingData(response.data)
          
          // Track successful load
          if (window.faroTracker) {
            window.faroTracker.trackEvent('order_tracking_loaded', {
              order_id: orderId,
              status: response.data.status,
              load_time: duration
            })
          }
        } else if (response.status === 404) {
          setError('Order not found. Please check your order ID.')
          
          if (window.faroTracker) {
            window.faroTracker.trackEvent('order_tracking_not_found', {
              order_id: orderId
            })
          }
        } else {
          setError('Unable to load tracking information. Please try again later.')
        }
      } catch (err) {
        setError('Unable to load tracking information. Please try again later.')
        
        if (window.faroTracker) {
          window.faroTracker.trackError(err, {
            context: 'order_tracking_load',
            order_id: orderId
          })
        }
      } finally {
        setLoading(false)
      }
    }

    fetchTrackingData()
  }, [orderId])

  const getStatusIcon = (status) => {
    const statusMap = {
      'order_placed': 'check circle',
      'processing': 'sync',
      'shipped': 'truck',
      'out_for_delivery': 'shipping fast',
      'delivered': 'flag checkered'
    }
    return statusMap[status] || 'question'
  }

  const getStatusColor = (status) => {
    const colorMap = {
      'order_placed': 'green',
      'processing': 'blue',
      'shipped': 'orange',
      'out_for_delivery': 'violet',
      'delivered': 'green'
    }
    return colorMap[status] || 'grey'
  }

  const getStatusText = (status) => {
    const textMap = {
      'order_placed': 'Order Placed',
      'processing': 'Processing',
      'shipped': 'Shipped',
      'out_for_delivery': 'Out for Delivery',
      'delivered': 'Delivered'
    }
    return textMap[status] || 'Unknown'
  }

  if (loading) {
    return (
      <Container>
        <SectionHeader />
        <Segment textAlign='center' padded='very'>
          <Loader active inline='centered'>Loading tracking information...</Loader>
        </Segment>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <SectionHeader />
        <Message error>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      </Container>
    )
  }

  if (!trackingData) {
    return null
  }

  const statusSteps = ['order_placed', 'processing', 'shipped', 'out_for_delivery', 'delivered']
  const currentStepIndex = statusSteps.indexOf(trackingData.status)

  return (
    <Container>
      <SectionHeader />
      
      <Segment>
        <Header as='h2' icon textAlign='center'>
          <Icon name='shipping fast' circular />
          <Header.Content>
            Track Your Order
            <Header.Subheader>Order ID: {orderId}</Header.Subheader>
          </Header.Content>
        </Header>

        <Divider />

        {/* Current Status Banner */}
        <Message 
          color={getStatusColor(trackingData.status)}
          icon={getStatusIcon(trackingData.status)}
          header={getStatusText(trackingData.status)}
          content={trackingData.status_message}
        />

        {/* Progress Steps */}
        <Step.Group fluid>
          {statusSteps.map((step, index) => (
            <Step
              key={step}
              active={index === currentStepIndex}
              completed={index < currentStepIndex}
              disabled={index > currentStepIndex}
            >
              <Icon name={getStatusIcon(step)} />
              <Step.Content>
                <Step.Title>{getStatusText(step)}</Step.Title>
              </Step.Content>
            </Step>
          ))}
        </Step.Group>

        <Divider />

        {/* Order Details */}
        <Grid stackable columns={2}>
          <Grid.Column>
            <Segment>
              <Header as='h3'>
                <Icon name='box' />
                Order Details
              </Header>
              <Table definition>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell>Order Date</Table.Cell>
                    <Table.Cell>{trackingData.order_date}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Estimated Delivery</Table.Cell>
                    <Table.Cell>
                      <strong>{trackingData.estimated_delivery}</strong>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Carrier</Table.Cell>
                    <Table.Cell>{trackingData.carrier}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Tracking Number</Table.Cell>
                    <Table.Cell>
                      <Label color='blue'>{trackingData.tracking_number}</Label>
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Segment>
              <Header as='h3'>
                <Icon name='map marker alternate' />
                Delivery Address
              </Header>
              <p>
                {trackingData.delivery_address.name}<br />
                {trackingData.delivery_address.street}<br />
                {trackingData.delivery_address.city}, {trackingData.delivery_address.state} {trackingData.delivery_address.zip}<br />
                {trackingData.delivery_address.country}
              </p>
            </Segment>
          </Grid.Column>
        </Grid>

        {/* Tracking Timeline */}
        <Segment>
          <Header as='h3'>
            <Icon name='history' />
            Tracking History
          </Header>
          
          <Step.Group vertical fluid>
            {trackingData.tracking_events.map((event, index) => (
              <Step key={index} active={index === 0}>
                <Icon name='circle' size='small' />
                <Step.Content>
                  <Step.Title>{event.status}</Step.Title>
                  <Step.Description>
                    <strong>{event.location}</strong> - {event.timestamp}
                    {event.notes && (
                      <div style={{ marginTop: '0.5em', color: '#666' }}>
                        <Icon name='info circle' /> {event.notes}
                      </div>
                    )}
                  </Step.Description>
                </Step.Content>
              </Step>
            ))}
          </Step.Group>
        </Segment>

        {/* Package Contents */}
        {trackingData.items && trackingData.items.length > 0 && (
          <Segment>
            <Header as='h3'>
              <Icon name='cube' />
              Package Contents ({trackingData.items.length} items)
            </Header>
            <Table basic='very'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Item</Table.HeaderCell>
                  <Table.HeaderCell textAlign='right'>Quantity</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {trackingData.items.map((item, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>{item.name}</Table.Cell>
                    <Table.Cell textAlign='right'>{item.quantity}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Segment>
        )}
      </Segment>
    </Container>
  )
}

export default TrackOrderPage


